import { Product, InventoryCategory } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

// Product categoryId holds the category documentId (relation), older data may hold the numeric id
const findCategory = (categories: InventoryCategory[], categoryId: string) =>
  categories.find(c => c.documentId === categoryId || String(c.id) === String(categoryId));

const isInCategory = (product: Product, category: InventoryCategory) =>
  product.categoryId === category.documentId || String(product.categoryId) === String(category.id);

function downloadCsv(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportProductsCsv(products: Product[], categories: InventoryCategory[]) {
  const headers = ['Code', 'Name', 'Description', 'Category', 'Quantity', 'Unit', 'Unit Price', 'Currency', 'Min Stock', 'Max Stock', 'Supplier', 'Location', 'Status', 'Last Restock Date'];
  const csvData = products.map(product => {
    const category = findCategory(categories, product.categoryId);
    return [product.code, product.name, product.description || '', category?.name || '', product.quantity, product.unit, product.unitPrice, resolveCurrencyCode(product.currency), product.minStockLevel, product.maxStockLevel, product.supplier || '', product.location || '', product.isActive ? 'Active' : 'Inactive', product.lastRestockDate || ''];
  });
  const csvContent = [headers.join(','), ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
  downloadCsv(csvContent, `inventory_products_${new Date().toISOString().split('T')[0]}.csv`);
}

export function exportCategoriesCsv(categories: InventoryCategory[], products: Product[]) {
  const headers = ['Name', 'Description', 'Product Count', 'Status'];
  const csvData = categories.map(category => {
    const productCount = products.filter(p => isInCategory(p, category)).length;
    return [category.name, category.description || '', productCount, category.isActive ? 'Active' : 'Inactive'];
  });
  const csvContent = [headers.join(','), ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
  downloadCsv(csvContent, `inventory_categories_${new Date().toISOString().split('T')[0]}.csv`);
}

export function buildProductPrintData(products: Product[], categories: InventoryCategory[]) {
  return products.map(product => {
    const category = findCategory(categories, product.categoryId);
    return { 'Code': product.code, 'Name': product.name, 'Description': product.description || '', 'Category': category?.name || '', 'Quantity': product.quantity, 'Unit': product.unit, 'Unit Price': product.unitPrice, 'Currency': resolveCurrencyCode(product.currency), 'Min Stock': product.minStockLevel, 'Max Stock': product.maxStockLevel, 'Supplier': product.supplier || '', 'Location': product.location || '', 'Status': product.isActive ? 'Active' : 'Inactive', 'Last Restock Date': product.lastRestockDate || '' };
  });
}

export function buildCategoryPrintData(categories: InventoryCategory[], products: Product[]) {
  return categories.map(category => {
    const productCount = products.filter(p => isInCategory(p, category)).length;
    return { 'Name': category.name, 'Description': category.description || '', 'Product Count': productCount, 'Status': category.isActive ? 'Active' : 'Inactive' };
  });
}

export function parseImportedProducts(text: string, categories: InventoryCategory[]): Product[] {
  const lines = text.split('\n').filter(line => line.trim());
  const importedProducts: Product[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
    if (values.length < 8) continue;
    const categoryName = values[3];
    const category = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase());
    importedProducts.push({
      id: Date.now().toString() + i, code: values[0], name: values[1], description: values[2],
      categoryId: category?.documentId || category?.id || categories[0]?.documentId || categories[0]?.id || '',
      quantity: parseFloat(values[4]) || 0,
      // CSV holds a currency code, not a relation documentId - left unset on import
      unit: values[5], unitPrice: parseFloat(values[6]) || 0, currency: '',
      minStockLevel: parseFloat(values[8]) || 0, maxStockLevel: parseFloat(values[9]) || 100,
      supplier: values[10], location: values[11], isActive: values[12]?.toLowerCase() === 'active',
      lastRestockDate: values[13], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    });
  }
  return importedProducts;
}
